'use client'
import { apiSubscribeNewsletter } from '@/apis/page_optimization';
import { faPaperPlane, FontAwesomeIcon } from '@/constants/icons';
import React, { useState } from 'react'
import { toast } from "react-toastify";
import { RecaptchaProvider } from '@/components/shared/RecaptchaProvider';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { apiVerifyCaptcha } from '@/apis/captcha';

interface Props {
  companyId: string;
  domain?: string;
}

const FooterNewsletterInner = ({ companyId, domain = '' }: Props) => {
  const [email, setEmail] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const { executeRecaptcha } = useGoogleReCaptcha();

  const validateEmail = (emailStr: string) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(emailStr);
  };

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      toast.error('Email field is required.', { autoClose: 2000 });
      return;
    }

    if (!validateEmail(email)) {
      toast.error("Please enter a valid email address.", { autoClose: 2000 });
      return;
    }

    setLoading(true);

    if (executeRecaptcha) {
      try {
        const recaptchaToken = await executeRecaptcha('newsletter_form');
        if (recaptchaToken) {
          const captchaRes = await apiVerifyCaptcha(domain, recaptchaToken);
          if (captchaRes && captchaRes.verified === false) {
            toast.error('Captcha verification failed. Please try again.', { autoClose: 2000 });
            setLoading(false);
            return;
          }
        }
      } catch (err) {
        console.warn('reCAPTCHA execution skipped:', err);
      }
    }

    try {

      const response = await apiSubscribeNewsletter(companyId, email);
      const resMsg = typeof response?.message === 'string' ? response.message : '';

      const isSuccess =
        (response?.status === true ||
          response?.status === 200 ||
          resMsg === "Subscribed successfully" ||
          resMsg === "Subscribed successfully!") &&
          !resMsg.includes("Server responded with status");

      if (isSuccess) {
        toast.success("Thank you for your feedback!", { autoClose: 2000 });
        setEmail("");
      } else {
        toast.info("You have already subscribed to our newsletter.", { autoClose: 2500 });
        setEmail("");
      }
    } catch (error: any) {
      console.error("Error subscribing:", error);
      toast.info("You have already subscribed to our newsletter.", { autoClose: 2500 });
      setEmail("");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      className="relative mt-3 w-full max-w-full sm:max-w-[320px]"
      onSubmit={handleSubscribe}
      autoComplete="off"
    >
      <div className="relative flex items-center p-1 sm:p-1.5 bg-slate-950/80 backdrop-blur-md rounded-2xl border border-slate-800/80 shadow-inner focus-within:border-indigo-400 focus-within:ring-2 focus-within:ring-indigo-500/20 transition-all duration-300">
        <input
          type="email"
          name="email"
          aria-label="Email address"
          placeholder="Enter email..."
          className="w-full min-w-0 flex-1 bg-transparent px-2.5 py-1.5 text-slate-100 text-xs outline-none placeholder:text-slate-500 font-medium"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={loading}
        />
        <button
          type="submit"
          disabled={loading}
          aria-label="Subscribe to newsletter"
          className={`
            flex items-center justify-center rounded-xl px-2 sm:px-2.5 py-1.5 sm:py-2 shrink-0
            transition-all duration-300 active:scale-95 text-[11px] sm:text-xs font-bold uppercase tracking-wider
            ${loading
              ? 'bg-slate-800 text-slate-500 cursor-not-allowed'
              : 'bg-gradient-to-r from-blue-600 via-indigo-600 to-indigo-700 hover:from-blue-500 hover:to-indigo-600 text-white shadow-md shadow-indigo-600/35 hover:shadow-indigo-500/60 hover:scale-[1.02]'}
          `}
        >
          {loading ? (
            <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            <div className="flex items-center gap-1.5">
              <FontAwesomeIcon icon={faPaperPlane} className="w-3 h-3" />
            </div>
          )}
        </button>
      </div>
    </form>
  )
}

const FooterNewsletter = (props: Props) => {
  return (
    <RecaptchaProvider domain={props.domain || ''}>
      <FooterNewsletterInner {...props} />
    </RecaptchaProvider>
  )
}

export default FooterNewsletter